import { routes } from 'shared/routes';

/** Query param appended to the login URL to trigger the session-expired toast. */
export const SESSION_EXPIRED_QUERY_PARAM = 'sessionExpired';

/**
 * Query param carrying the post-login return path.
 */
export const LOGIN_REDIRECT_QUERY_PARAM = 'redirect';

/**
 * Builds the login URL for a session-expiry redirect: `sessionExpired=true`
 * triggers the toast on the login page; `redirect` preserves the user's
 * location for post-login return. The `redirect` value is validated by
 * `loginSearchSchema` (features/auth/Login) on the consumer side — invalid
 * values are dropped there and login falls back to the default destination.
 *
 * @param returnTo - Current internal location (`pathname + search + hash`).
 */
export const buildSessionExpiredLoginUrl = (returnTo: string) => {
  const params = new URLSearchParams({
    [SESSION_EXPIRED_QUERY_PARAM]: 'true',
    [LOGIN_REDIRECT_QUERY_PARAM]: returnTo
  });

  return `${routes.login()}?${params.toString()}`;
};

/** Same-origin path of the monolith OAuth authorize endpoint (proxied via nginx `/api/`). */
const OAUTH_AUTHORIZE_PATH = '/api/oauth/v1/authorize';

/**
 * True when a validated `redirect` value targets the OAuth authorize endpoint,
 * which is backend-served and needs a full-page navigation — SPA routing would
 * 404, the path only exists behind the `/api/` proxy. Exact-path match (with
 * optional query) so sibling paths like `.../authorize-evil` don't qualify.
 *
 * @param redirect - Validated `redirect` search param (`loginSearchSchema`).
 */
export const isOAuthAuthorizeRedirect = (redirect: string) =>
  redirect === OAUTH_AUTHORIZE_PATH || redirect.startsWith(`${OAUTH_AUTHORIZE_PATH}?`);
